import { useCallback } from 'react';

// ==========================================
// useBankInput
// ==========================================
// Patrón de entrada numérica bancaria estilo Banamex:
//   - Al enfocar: muestra valor con 2 decimales, cursor antes del punto
//   - Teclear dígito antes del punto: inserta entero
//   - Teclear punto/coma: mueve cursor después del punto
//   - Teclear dígito después del punto: rellena decimales
//   - Backspace antes del punto: borra último entero
//   - Backspace en punto: regresa cursor antes del punto
//   - Backspace en decimales: reemplaza con 0
//   - Nunca queda vacío: mínimo 0.00
//   - Ctrl+A, Tab, Enter, flechas: comportamiento nativo
//
// Callbacks:
//   onUpdate(val) → cada tecla → actualiza display en detailData
//   onCommit(val) → onBlur    → dispara validación de comisión
// ==========================================

const useBankInput = (value, onUpdate, onCommit) => {

  // Formatea número para edición — sin separador de miles
  const formatEdit = useCallback((val) => {
    const num = parseFloat(val) || 0;
    return num.toFixed(2);
  }, []);

  const handleFocus = useCallback((e) => {
    const formatted = formatEdit(value);
    e.target.value = formatted;
    const dotIndex = formatted.indexOf('.');
    requestAnimationFrame(() => {
      if (e.target === document.activeElement) {
        e.target.setSelectionRange(dotIndex, dotIndex);
      }
    });
  }, [value, formatEdit]);

  const handleKeyDown = useCallback((e) => {
    const input    = e.target;
    const val      = input.value;
    const cursor   = input.selectionStart;
    const dotIndex = val.indexOf('.');

    // Permitir navegación y atajos nativos
    if (['Tab', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(e.key)) return;
    if (e.ctrlKey || e.metaKey) return;
    if (e.key === 'Escape') return;

    e.preventDefault();

    const isDigit     = /^[0-9]$/.test(e.key);
    const isDot       = e.key === '.' || e.key === ',';
    const isBackspace = e.key === 'Backspace';
    const isDelete    = e.key === 'Delete';

    if (isDigit) {
      if (cursor <= dotIndex) {
        // ── Zona entera ──
        const enteros   = val.slice(0, dotIndex).replace(/[^0-9]/g, '');
        const decimales = val.slice(dotIndex + 1);
        if (enteros.length >= 10) return; // máx 10 dígitos enteros
        const newEnteros = enteros === '0' ? e.key : enteros + e.key;
        const newVal     = `${newEnteros}.${decimales}`;
        input.value      = newVal;
        const newCursor  = newVal.indexOf('.');
        input.setSelectionRange(newCursor, newCursor);
        onUpdate(parseFloat(newVal) || 0);
      } else {
        // ── Zona decimal ──
        const enteros    = val.slice(0, dotIndex);
        const decimales  = val.slice(dotIndex + 1).replace(/[^0-9]/g, '');
        const posDecimal = cursor - dotIndex - 1;
        if (posDecimal >= 2) return; // máx 2 decimales
        const decArr       = decimales.padEnd(2, '0').split('');
        decArr[posDecimal] = e.key;
        const newVal       = `${enteros}.${decArr.join('').slice(0, 2)}`;
        input.value        = newVal;
        const newCursor    = dotIndex + posDecimal + 2;
        input.setSelectionRange(newCursor, newCursor);
        onUpdate(parseFloat(newVal) || 0);
      }
      return;
    }

    if (isDot) {
      // Mover cursor justo después del punto
      input.setSelectionRange(dotIndex + 1, dotIndex + 1);
      return;
    }

    if (isBackspace) {
      if (cursor <= dotIndex && cursor > 0) {
        // ── Borrar entero ──
        const enteros    = val.slice(0, dotIndex).replace(/[^0-9]/g, '');
        const decimales  = val.slice(dotIndex + 1);
        const newEnteros = enteros.length > 1 ? enteros.slice(0, -1) : '0';
        const newVal     = `${newEnteros}.${decimales}`;
        input.value      = newVal;
        const newCursor  = newVal.indexOf('.');
        input.setSelectionRange(newCursor, newCursor);
        onUpdate(parseFloat(newVal) || 0);
      } else if (cursor === dotIndex + 1) {
        // ── Justo después del punto → regresar antes ──
        input.setSelectionRange(dotIndex, dotIndex);
      } else if (cursor > dotIndex + 1) {
        // ── Borrar decimal → reemplazar con 0 ──
        const enteros    = val.slice(0, dotIndex);
        const decimales  = val.slice(dotIndex + 1).replace(/[^0-9]/g, '').padEnd(2, '0').split('');
        const posDecimal = cursor - dotIndex - 2;
        if (posDecimal >= 0 && posDecimal < 2) {
          decimales[posDecimal] = '0';
          const newVal    = `${enteros}.${decimales.join('').slice(0, 2)}`;
          input.value     = newVal;
          const newCursor = dotIndex + posDecimal + 1;
          input.setSelectionRange(newCursor, newCursor);
          onUpdate(parseFloat(newVal) || 0);
        }
      }
      return;
    }

    if (isDelete) {
      if (cursor < dotIndex) {
        // ── Borrar entero hacia adelante ──
        const enteros   = val.slice(0, dotIndex).replace(/[^0-9]/g, '');
        const decimales = val.slice(dotIndex + 1);
        const arr       = enteros.split('');
        arr.splice(cursor, 1);
        const newEnteros = arr.join('') || '0';
        const newVal     = `${newEnteros}.${decimales}`;
        input.value      = newVal;
        input.setSelectionRange(cursor, cursor);
        onUpdate(parseFloat(newVal) || 0);
      } else if (cursor === dotIndex) {
        // ── Sobre el punto → mover cursor adelante ──
        input.setSelectionRange(dotIndex + 1, dotIndex + 1);
      } else if (cursor > dotIndex) {
        // ── Borrar decimal hacia adelante → reemplazar con 0 ──
        const enteros    = val.slice(0, dotIndex);
        const decimales  = val.slice(dotIndex + 1).replace(/[^0-9]/g, '').padEnd(2, '0').split('');
        const posDecimal = cursor - dotIndex - 1;
        if (posDecimal >= 0 && posDecimal < 2) {
          decimales[posDecimal] = '0';
          const newVal = `${enteros}.${decimales.join('').slice(0, 2)}`;
          input.value  = newVal;
          input.setSelectionRange(cursor, cursor);
          onUpdate(parseFloat(newVal) || 0);
        }
      }
    }

  }, [onUpdate]);

  const handleBlur = useCallback((e) => {
    const val = parseFloat(e.target.value) || 0;
    onCommit?.(val);
  }, [onCommit]);

  return {
    formatEdit,
    handleFocus,
    handleKeyDown,
    handleBlur
  };
};

export default useBankInput;